import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Plus, Search, Eye, Copy, FileDown, ChevronLeft, ChevronRight, Ban } from 'lucide-react'
import toast from 'react-hot-toast'

import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { TableColumnVisibilityMenu } from '@/components/shared/TableColumnVisibilityMenu'
import { ListLoader } from '@/components/shared/ListLoader'
import type { UnsellableMovementRow } from '../types'
import { useAuthStore } from '@/store/auth.store'
import { unsellableInventoryMovementService } from '@/services/unsellableInventoryMovementService'

const PAGE_SIZE = 10

type ColumnKey =
  | 'code'
  | 'warehouse'
  | 'item'
  | 'batch'
  | 'previousQuantity'
  | 'damaged'
  | 'expired'
  | 'newQuantity'
  | 'date'

const ALL_COLUMNS: { key: ColumnKey; labelKey: string }[] = [
  { key: 'code', labelKey: 'unsellableInventory.columns.code' },
  { key: 'warehouse', labelKey: 'unsellableInventory.columns.warehouse' },
  { key: 'item', labelKey: 'unsellableInventory.columns.item' },
  { key: 'batch', labelKey: 'unsellableInventory.columns.batch' },
  { key: 'previousQuantity', labelKey: 'unsellableInventory.columns.previousQuantity' },
  { key: 'damaged', labelKey: 'unsellableInventory.columns.damaged' },
  { key: 'expired', labelKey: 'unsellableInventory.columns.expired' },
  { key: 'newQuantity', labelKey: 'unsellableInventory.columns.newQuantity' },
  { key: 'date', labelKey: 'unsellableInventory.columns.date' },
]

function formatListDate(iso: string, locale: string) {
  if (!iso) return '—'
  const d = new Date(`${iso}T12:00:00`)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(locale === 'ar' ? 'ar-SA' : 'en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

export const UnsellableInventoryList: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const selectedBranchId = Number(user?.selectedBranch?.branchId ?? 0)

  const [rows, setRows] = useState<UnsellableMovementRow[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [searchQuery, setSearchQuery] = useState('')
  const [visibleColumns, setVisibleColumns] = useState<Record<ColumnKey, boolean>>({
    code: true,
    warehouse: true,
    item: true,
    batch: true,
    previousQuantity: true,
    damaged: true,
    expired: true,
    newQuantity: true,
    date: true,
  })

  const prevBranchIdRef = useRef<number | null>(null)

  useEffect(() => {
    if (!selectedBranchId) {
      setRows([])
      setTotalCount(0)
      prevBranchIdRef.current = null
      return
    }
    const prev = prevBranchIdRef.current
    if (prev !== null && prev !== selectedBranchId) {
      prevBranchIdRef.current = selectedBranchId
      setPageNumber(1)
      return
    }
    prevBranchIdRef.current = selectedBranchId

    let cancelled = false
    setLoading(true)
    void unsellableInventoryMovementService
      .getAll(selectedBranchId, pageNumber, PAGE_SIZE)
      .then((res) => {
        if (!cancelled) {
          setRows(res.items)
          setTotalCount(res.totalCount)
        }
      })
      .catch((e: any) => {
        if (!cancelled) {
          setRows([])
          setTotalCount(0)
          const msg = e?.response?.data?.message
          toast.error(msg || t('unsellableInventory.listLoadFailed'))
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [selectedBranchId, pageNumber, t])

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) =>
      [
        r.code,
        r.warehouseName,
        r.itemLabel,
        r.batchLabel,
        String(r.previousQuantity),
        String(r.damaged),
        String(r.expired),
        String(r.newQuantity),
        r.movementDate,
      ].some((f) => String(f).toLowerCase().includes(q)),
    )
  }, [rows, searchQuery])

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
  const page = Math.min(pageNumber, totalPages)

  const startIdx = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
  const endIdx = Math.min(page * PAGE_SIZE, totalCount)

  const toggleColumn = (columnId: string) => {
    const key = columnId as ColumnKey
    if (!ALL_COLUMNS.some((c) => c.key === key)) return
    setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const columnMenuItems = useMemo(
    () => ALL_COLUMNS.map((c) => ({ id: c.key, label: t(c.labelKey) })),
    [t],
  )

  const handleAdd = () => navigate('/inventory/unsellable-movement/new')
  const handleView = (row: UnsellableMovementRow) =>
    navigate(`/inventory/unsellable-movement/${row.id}/view`, { state: { movement: row } })

  const csvEscape = (cell: string | number) => `"${String(cell).replace(/"/g, '""')}"`

  const handleCopy = () => {
    if (filtered.length === 0) {
      toast.error(t('unsellableInventory.nothingToCopy'))
      return
    }
    const header = ALL_COLUMNS.filter((c) => visibleColumns[c.key]).map((c) => t(c.labelKey))
    header.push(t('unsellableInventory.columns.view'))
    const lines = filtered.map((r) => {
      const cells: (string | number)[] = []
      if (visibleColumns.code) cells.push(r.code)
      if (visibleColumns.warehouse) cells.push(r.warehouseName)
      if (visibleColumns.item) cells.push(r.itemLabel)
      if (visibleColumns.batch) cells.push(r.batchLabel)
      if (visibleColumns.previousQuantity) cells.push(r.previousQuantity)
      if (visibleColumns.damaged) cells.push(r.damaged)
      if (visibleColumns.expired) cells.push(r.expired)
      if (visibleColumns.newQuantity) cells.push(r.newQuantity)
      if (visibleColumns.date) cells.push(r.movementDate)
      return cells.join('\t')
    })
    void navigator.clipboard.writeText([header.join('\t'), ...lines].join('\n'))
    toast.success(t('unsellableInventory.copied'))
  }

  const handleExportCSV = () => {
    if (filtered.length === 0) {
      toast.error(t('unsellableInventory.nothingToExport'))
      return
    }
    const header = ALL_COLUMNS.filter((c) => visibleColumns[c.key])
      .map((c) => csvEscape(t(c.labelKey)))
      .join(',')
    const body = filtered
      .map((r) => {
        const cells: (string | number)[] = []
        if (visibleColumns.code) cells.push(r.code)
        if (visibleColumns.warehouse) cells.push(r.warehouseName)
        if (visibleColumns.item) cells.push(r.itemLabel)
        if (visibleColumns.batch) cells.push(r.batchLabel)
        if (visibleColumns.previousQuantity) cells.push(r.previousQuantity)
        if (visibleColumns.damaged) cells.push(r.damaged)
        if (visibleColumns.expired) cells.push(r.expired)
        if (visibleColumns.newQuantity) cells.push(r.newQuantity)
        if (visibleColumns.date) cells.push(r.movementDate)
        return cells.map(csvEscape).join(',')
      })
      .join('\n')
    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `unsellable_inventory_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
    toast.success(t('unsellableInventory.exported'))
  }

  const visibleCount = Object.values(visibleColumns).filter(Boolean).length
  const loc = i18n.language?.startsWith('ar') ? 'ar' : 'en'

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Ban className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('unsellableInventory.title')}</h1>
            <p className="text-muted-foreground text-sm">{t('unsellableInventory.listSubtitle')}</p>
          </div>
        </div>

        <Button onClick={handleAdd} className="w-full sm:w-auto shrink-0">
          <Plus className="mr-2 h-4 w-4" />
          {t('unsellableInventory.add')}
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
            <div className="flex-1">
              <label className="mb-2 block text-sm font-medium">{t('unsellableInventory.search')}</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={t('unsellableInventory.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value)
                    setPageNumber(1)
                  }}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <TableColumnVisibilityMenu
                columns={columnMenuItems}
                visibility={visibleColumns}
                onToggle={toggleColumn}
                triggerLabel={t('unsellableInventory.columnVisibility')}
                menuTitle={t('unsellableInventory.columnsToggle')}
              />

              <Button variant="outline" size="sm" onClick={handleExportCSV}>
                <FileDown className="mr-2 h-4 w-4" />
                CSV
              </Button>
              <Button variant="outline" size="sm" onClick={handleCopy}>
                <Copy className="mr-2 h-4 w-4" />
                {t('unsellableInventory.copy')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                  {visibleColumns.code && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('unsellableInventory.columns.code')}
                    </TableHead>
                  )}
                  {visibleColumns.warehouse && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('unsellableInventory.columns.warehouse')}
                    </TableHead>
                  )}
                  {visibleColumns.item && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('unsellableInventory.columns.item')}
                    </TableHead>
                  )}
                  {visibleColumns.batch && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('unsellableInventory.columns.batch')}
                    </TableHead>
                  )}
                  {visibleColumns.previousQuantity && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('unsellableInventory.columns.previousQuantity')}
                    </TableHead>
                  )}
                  {visibleColumns.damaged && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('unsellableInventory.columns.damaged')}
                    </TableHead>
                  )}
                  {visibleColumns.expired && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('unsellableInventory.columns.expired')}
                    </TableHead>
                  )}
                  {visibleColumns.newQuantity && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('unsellableInventory.columns.newQuantity')}
                    </TableHead>
                  )}
                  {visibleColumns.date && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('unsellableInventory.columns.date')}
                    </TableHead>
                  )}
                  <TableHead className="h-12 w-[88px] bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                    {t('unsellableInventory.columns.view')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={visibleCount + 1} className="p-0">
                      <ListLoader message={t('common.loading', { defaultValue: 'Loading...' })} />
                    </TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleCount + 1} className="py-12 text-center text-muted-foreground">
                      {!selectedBranchId
                        ? t('unsellableInventory.selectBranchForWarehouses')
                        : t('unsellableInventory.emptyTable')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((row) => (
                    <TableRow key={row.id} className="border-b transition-colors hover:bg-primary/5 dark:hover:bg-primary/10">
                      {visibleColumns.code && <TableCell className="px-4 py-2 text-center text-sm">{row.code}</TableCell>}
                      {visibleColumns.warehouse && (
                        <TableCell className="px-4 py-2 text-center text-sm">{row.warehouseName}</TableCell>
                      )}
                      {visibleColumns.item && <TableCell className="px-4 py-2 text-center text-sm">{row.itemLabel}</TableCell>}
                      {visibleColumns.batch && <TableCell className="px-4 py-2 text-center text-sm">{row.batchLabel}</TableCell>}
                      {visibleColumns.previousQuantity && (
                        <TableCell className="px-4 py-2 text-center text-sm">{row.previousQuantity}</TableCell>
                      )}
                      {visibleColumns.damaged && <TableCell className="px-4 py-2 text-center text-sm">{row.damaged}</TableCell>}
                      {visibleColumns.expired && <TableCell className="px-4 py-2 text-center text-sm">{row.expired}</TableCell>}
                      {visibleColumns.newQuantity && (
                        <TableCell className="px-4 py-2 text-center text-sm">{row.newQuantity}</TableCell>
                      )}
                      {visibleColumns.date && (
                        <TableCell className="px-4 py-2 text-center text-sm">
                          {formatListDate(row.movementDate, loc)}
                        </TableCell>
                      )}
                      <TableCell className="px-2 py-2 text-center">
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleView(row)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-col items-center justify-between gap-4 border-t px-4 py-4 sm:flex-row sm:px-6">
            <p className="text-center text-sm text-muted-foreground sm:text-left">
              {t('unsellableInventory.showing', { start: startIdx, end: endIdx, total: totalCount })}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPageNumber((p) => Math.max(1, p - 1))} disabled={page <= 1}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="min-w-[2rem] text-center text-sm font-medium">{page}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
